import {
    Component,
    Event,
    EventEmitter,
    h,
    Host,
    Listen,
    Method,
    Prop,
    State,
    Watch,
} from '@stencil/core';
import {
    ApolloThemeType,
    defaultTheme,
    getCachedLanguage,
    getCachedTheme,
    getEnvironment,
    MediaQueryListener,
    validateLanguageCode,
    validateThemeCode,
} from '@uipath/portal-shell-util';

import {
    AccountType,
    LanguageCode,
} from '../../constants/constants';
import { SERVICE_IDS } from '../../constants/defaultServiceOrder';
import { SettingClickedEvent } from '../../models/events/settingClickedEvent';
import { HelpMenuAction } from '../../models/helpActions';
import { ProductInfo } from '../../models/productInfo';
import { Tenant } from '../../models/tenant';
import { defaultMenuItems } from '../../store/defaultState';
import store from '../../store/store';
import { accountAndTenantsStore } from '../../utils/accountAndTenants/accountAndTenants';
import { isEnterpriseOrProOrTrial } from '../../utils/accountAndTenants/accountAndTenantsHelpers';
import { AccountAndTenantsInstanceResolver } from '../../utils/accountAndTenants/accountAndTenantsInstanceResolver';
import { initializeThemeCache } from '../../utils/cacheHelper';
import {
    getEnvConfigField,
    isNonCloud,
    isStandalone,
} from '../../utils/EnvironmentUtil';
import { Features } from '../../utils/featureFlags/featureFlagConstants';
import {
    getFeatureFlag,
    getFeatureFlags,
} from '../../utils/featureFlags/featureFlags';
import {
    caseInsensitiveCompare,
    parseArrayProps,
} from '../../utils/helper';
import {
    getLanguage,
    setLanguageNoBroadcast,
} from '../../utils/languageHelper';
import { t } from '../../utils/localization/loc';
import Logger from '../../utils/logger/logger';
import {
    getAccessToken,
    sessionStore,
} from '../../utils/session/sessionStore';
import {
    getTheme,
    setThemeNoBroadcast,
} from '../../utils/themeHelper';
import { getSpaceName } from '../../utils/urlHelper';
import { FOCUSABLE_ELEMENTS_QUERY } from '../ap-skip-content/constants';

@Component({
    tag: 'ap-shell',
    styleUrl: 'ap-shell.scss',
    shadow: true,
})
export class ApShell {

    // #region Properties
    /**
     * [Required] Product name displayed next to the UiPath logo
     * @memberof ApAppBar
     * @required
     */
    @Prop() productName: string;

    /**
     * Title displayed to the right of the cobrand image,
     * eg: Tenant Management, Datasets, Services, etc.
     * @memberof ApAppBar
     */
    @Prop() feature: string | undefined = undefined;

    /**
     * Enable trademark for your product name
     * @memberof ApAppBar
     * @default false
     */
    @Prop() showProductTrademark: boolean = false;

    /**
     * Enable trademark for the UiPath logo
     * @memberof ApAppBar
     * @default true
     */
    @Prop() showLogoTrademark: boolean = true;

    /**
     * Show Co-brand image in header
     * @memberof ApAppBar
     */
    @Prop() cobrandingLogoUrl: string | null = null;

    /**
      * Provide Co-brand-label as alt tag
      * @memberof ApAppBar
      */
    @Prop() cobrandingLogoAlt: string | undefined = undefined;

    /**
     * Shows the preview tag on the right of the product name
     * @memberof ApAppBar
     * @default false
     */
    @Prop() isPreview: boolean = false;

    /**
     * Override for the tenant color hexcode.
     * Color will a hexcode. Can be uppercase or lowercase
     * @memberof ApAppBar
     */
    @Prop() tenantColor: string | null = null;

    /**
     * Additional help menu items to render, in addition to the built-in menu items.
     * @memberof ApAppBar
     */
    @Prop() helpMenuItems: HelpMenuAction[] | string = [];

    /**
     * Timezone of the UI to show notification, default to browsers timezone or 'Etc/UTC' if not found
     * @memberof PortalNotification
     */
    @Prop() timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Etc/UTC';

    /**
     * Show slot end divider if slot end is going to be populated with content
     * @memberof ApAppBar
     */
    @Prop() showSlotEndDivider: boolean = false;

    /**
     * Show the preferences button and hide the language and theme picker
     * @memberof ApAppBar
     */
    @Prop() showPreferences: boolean = true;

    /**
     * Disables the product name from being clickable
     * @memberof ApAppBar
     */
    @Prop() disableProductClick: boolean = false;

    /**
     * Passes an href to the logo
     * @memberof ApAppBar
     */
    @Prop() logoClickHref?: string;

    /**
     * Passes an href to the feature name
     * @memberof ApAppBar
     */
    @Prop() featureNameClickHref?: string;

    /**
     * Shows the cancel button on the per-service trial bar
     * @memberof ApAppBar
     */
    @Prop() isPerServiceTrialBarCancelable: boolean = false;

    /**
     * The active service that is highlighted in the left nav and the waffle menu
     * @memberof ApNav
     */
    @Prop({ mutable: true }) active: string;

    /**
     * Services(s) where the serviceClicked event is triggered and navigation to the service is prevented.
     * @memberof ApShell
     */
    @Prop() emitEventAndPreventNavigationServices: string = '';

    /**
     * (MSI or Standalone only) Services to display in the shell
     * @memberof ApShell
     */
    @Prop() servicesOverride: string = '';

    /**
     * Focus mode (hide the left nav)
     * @memberof ApShell
     */
    @Prop() focusMode: boolean = false;

    /**
     * Show the help menu
     * @memberof ApShell
     * @default true
     */
    @Prop() showHelpMenu: boolean = true;

    /**
     * Show the profile avatar and profile menu
     * @memberof ApShell
     * @default true
     */
    @Prop() showProfileMenu: boolean = true;

    /**
     * Show the enterprise trial
     * @memberof ApShell
     */
    @Prop() showEnterpriseTrial: boolean = false;

    /**
     * End date of the enterprise trial
     * @memberof ApShell
     */
    @Prop() enterpriseTrialEndDate: number = 0;

    /**
     * Theme override
     * @memberof ApShell
     */
    @Prop() themeOverride: ApolloThemeType;

    /**
     * Language override
     * @memberof ApShell
     */
    @Prop() languageOverride: LanguageCode;

    /**
     * Show the waffle menu
     * @memberof ApShell
     */
    @Prop() showNavMenuToggle: boolean = true;

    /**
     * The email to show in the profile dropdown
     * @memberof PortalAvatar
     */
    @Prop() emailOverride?: string;

    /**
     * The full name to show in the profile dropdown
     * @memberof PortalAvatar
     */
    @Prop() fullNameOverride?: string;

    /**
     * Product info to show in the profile dropdown
     * @memberof PortalProfileMenu
     */
    @Prop() productInfo?: ProductInfo | string;

    /**
     * Add option to remove the skip to content button (in case consumers have their own)
     *
     * @default true
     * @memberof ApShell
     */
    @Prop() useSkipToContent: boolean = true;

    /**
     * The element to be focused when the skip content button is clicked.
     * It can be directly a focusable element or a container element which contains focusable elements.
     *
     * @memberof ApSkipContent
     * @default FOCUSABLE_ELEMENTS_QUERY
     */
    @Prop() skipToTarget = FOCUSABLE_ELEMENTS_QUERY;

    /**
     * Timeout in ms for trying to find a focusable element within `skipToTarget`.
     *
     * @memberof ApSkipContent
     * @default 1000
     */
    @Prop() waitForSkipTarget = 1000;

    /**
     * Hide the tenant picker
     * @memberof ApShell
     * @default false
     */
    @Prop() hideTenantPicker: boolean = false;

    // #endregion

    // #region State

    @State() isFocusSize: boolean = false;

    @State() isMenuExpanded: boolean = false;

    // # endregion

    // #region Events
    /**
    * Triggered when a service is clicked
    * @event ApNav
    */
    @Event({
        eventName: 'serviceClicked',
        composed: true,
        cancelable: true,
        bubbles: true,
    }) onServiceClickedCustomEvent: EventEmitter<SettingClickedEvent>;

    /**
     * Triggered when the logo is clicked on the app bar.
     * The default event generated from the anchor tag is in the event.detail as event.detail.originalEvent.
     * If the originalEvent is not cancelled with preventDefault, the default link navigation will occur.
     * @memberof ApAppBar
     */
    @Event({
        eventName: 'logoClicked',
        composed: true,
        cancelable: true,
        bubbles: true,
    }) onLogoClickedCustomEvent: EventEmitter;

    /**
     * Triggered when the feature name is clicked on the app bar.
     * The default event generated from the anchor tag is in the event.detail as event.detail.originalEvent.
     * If the originalEvent is not cancelled with preventDefault, the default link navigation will occur.
     * @memberof ApAppBar
     */
    @Event({
        eventName: 'featureNameClicked',
        composed: true,
        cancelable: true,
        bubbles: true,
    }) onFeatureNameClickedCustomEvent: EventEmitter;

    /**
     * Triggered when a help item action is clicked
     * @memberof PortalHelp
     */
    @Event({
        eventName: 'helpItemClicked',
        composed: false,
        cancelable: true,
        bubbles: true,
    }) helpItemClicked: EventEmitter<{ id: string }>;

    // #endregion

    private _slotRef: HTMLSlotElement;

    private apDialog: HTMLApDialogElement;

    /**
     * Toggle menu surface visibility
     * @method ApShell
     */
    @Method()
    async handleOpenDialog() {
        this.apDialog.toggleDialog();
    }

    // #region Fields and States
    // #endregion

    // #region Lifecycle Methods
    connectedCallback() {
        // Sign in if it hasn't been done yet and the mode isn't standalone
        // This is a fail safe in case the user doesn't use the global script
        if (!sessionStore.get('isSignInInitialized') && !isStandalone()) {
            store.set('mode', 'auto');
        }

        // Handle theme and language change events
        initializeThemeCache();
        if (this.themeOverride) {
            store.set('overrideTheme', this.themeOverride);
        }
        this.setPortalNavTheme();
        if (this.languageOverride) {
            store.set('overrideLocale', this.languageOverride);
        }
        this.setPortalNavLanguage();

        // Handle overridden services for onprem
        if (this.servicesOverride) {
            AccountAndTenantsInstanceResolver.Instance.updateMenuItems({ overrideServices: parseArrayProps(this.servicesOverride) });
        }

        const [
            enableLeftNavRedesign,
            enableLeftNavMenuExpansion,
        ] = getFeatureFlags([
            Features.EnableLeftNavRedesign.name,
            Features.EnableLeftNavMenuExpansion.name,
        ]);

        // Handle media query for left nav redesign
        if (enableLeftNavRedesign && enableLeftNavMenuExpansion) {
            MediaQueryListener.subscribe(this.handleFocusSize.bind(this), '(max-width: 1280px)');
        }

        // Put the setting click emitter into the store until V2 is deprecated
        store.set('settingClickEmitter', this.onServiceClickedCustomEvent);
    }

    disconnectedCallback() {
        const [
            enableLeftNavRedesign,
            enableLeftNavMenuExpansion,
        ] = getFeatureFlags([
            Features.EnableLeftNavRedesign.name,
            Features.EnableLeftNavMenuExpansion.name,
        ]);

        if (enableLeftNavRedesign && enableLeftNavMenuExpansion) {
            MediaQueryListener.unsubscribe(this.handleFocusSize.bind(this), '(max-width: 1280px)');
        }
    }
    // #endregion

    // #region Property Watchers

    @Watch('active')
    onActiveChange(newValue: string, oldValue: string) {
        if (!newValue || (newValue !== oldValue && !SERVICE_IDS[newValue])) {
            Logger.debug([ 'ap-shell', 'active' ], `new: ${newValue}, old: ${oldValue}`);
            this.active = oldValue || getSpaceName()?.toLowerCase();
        }
    }

    @Watch('servicesOverride')
    onServicesOverrideChange(newValue: string, oldValue: string) {
        if (newValue !== oldValue) {
            Logger.debug([ 'ap-shell', 'servicesOverride' ], `new: ${newValue}, old: ${oldValue}`);
            AccountAndTenantsInstanceResolver.Instance.updateMenuItems({ overrideServices: parseArrayProps(newValue) });
        }
    }

    // #endregion

    // #region Event Listeners

    /**
     * Function is triggered when tenant is changed.
     * Listen to the `optionSelected` event and set the current tenant as active.
     * @param _event
     */
    @Listen('optionSelected')
    optionSelected(_event: CustomEvent) {
        const tenant = _event.detail.name;
        Logger.info([ 'ap-shell', 'tenant', 'click' ], tenant, { state: store.state });
        AccountAndTenantsInstanceResolver.Instance.updateCurrentTenant(tenant);
    }
    // #endregion

    // #region Render Method
    render() {
        const apolloTheme = getTheme(store, this.themeOverride);
        const theme = apolloTheme.light ? 'light' : 'dark';

        const leftNavMenuExpandable = getFeatureFlag(Features.EnableLeftNavMenuExpansion.name);
        const leftNavRedesignEnabled = getFeatureFlag(Features.EnableLeftNavRedesign.name);

        const organization = accountAndTenantsStore.get('organization');

        // App Bar Properties
        const locale = getLanguage(store);
        const tenantList = accountAndTenantsStore.get('tenants');
        const cobrandingLogo = accountAndTenantsStore.state.organization?.[apolloTheme.light ? 'lightLogo' : 'darkLogo'];
        const currentTenantInfo = AccountAndTenantsInstanceResolver.Instance.getTenant(accountAndTenantsStore.get('currentTenant'));

        // Left Nav Properties
        const focusMode = this.isFocusMode();
        const serviceList = this.getServiceList();
        const expanded = leftNavMenuExpandable && leftNavRedesignEnabled && this.isMenuExpanded;
        let trialContentClass = 'trial-content';
        if (focusMode) {
            trialContentClass += ' focus-mode';
        } else if (expanded) {
            trialContentClass += ' expanded';
        }

        // Trial Bar Properties
        const perServiceTrialEndDate = this.getValidTrialServiceEndDate();

        // Visibility
        const isHelpIconVisible = this.showHelpMenu;
        const isNotificationVisible = this.isNotificationVisible();
        const isTenantPickerVisible = this.isTenantPickerVisible(tenantList);
        const accountType = organization?.accountType;
        const isEnterpriseTrialInternalVisible = this.isEnterpriseTrialInternalVisible(accountType);
        const showTimeoutDialog = (isEnterpriseOrProOrTrial(accountType) || isNonCloud()) && organization?.idleSessionTimeoutPeriod;

        return (
            <Host class={apolloTheme}>
                { this.useSkipToContent && (
                    <ap-skip-content
                        rootRef={this._slotRef?.assignedElements?.()?.find(el => !!el.querySelector(this.skipToTarget)) ?? document.body}
                        skipToTarget={this.skipToTarget}
                        waitForSkipTarget={this.waitForSkipTarget}
                        theme={theme}
                    ></ap-skip-content>
                )}
                <ap-app-bar
                    theme={theme}
                    language={locale}
                    feature={this.feature}
                    product={this.productName}
                    disableProductClick={this.disableProductClick}
                    isPreview={this.isPreview}
                    showProductTrademark={this.showProductTrademark}
                    showLogoTrademark={this.showLogoTrademark}
                    tenantColor={this.tenantColor ?? currentTenantInfo?.color}
                    cobrandingLogoUrl={this.cobrandingLogoUrl ?? cobrandingLogo}
                    cobrandingLogoAlt={this.cobrandingLogoAlt}
                    showSlotEndDivider={this.showSlotEndDivider}
                    onLogoClicked={(e) => this.onLogoClicked(e, this.onLogoClickedCustomEvent)}
                    logoClickHref={this.logoClickHref}
                    onFeatureNameClicked={(e) => this.onFeatureNameClicked(e, this.onFeatureNameClickedCustomEvent)}
                    featureNameClickHref={this.featureNameClickHref}
                >
                    {this.showNavMenuToggle && (
                        <ap-waffle-menu
                            slot="waffle-menu"
                            services={serviceList}
                            theme={theme}
                            language={locale}
                            preventOverlay={!focusMode}
                            onActionClicked={this.onExpandMenuToggled.bind(this)}
                        />
                    )}

                    <slot slot="slot-start" name="slot-start" />
                    <slot slot="slot-mid" name="slot-mid" />
                    <slot slot="slot-end" name="slot-end" />
                    { isHelpIconVisible && (
                        <portal-help
                            theme={theme}
                            slot="help-menu"
                            onHelpItemClicked={(event) => this.helpItemClicked.emit({ id: event.detail.id })}
                            helpItems={this.helpMenuItems}
                            serviceId={this.active}
                            language={locale}
                        />
                    )}
                    { isNotificationVisible && (
                        <portal-notification
                            slot="notifications"
                            timezone={this.timezone}
                            class={`notifications ${isHelpIconVisible ? 'with-help' : ''}`}
                        />
                    )}
                    { isTenantPickerVisible && (
                        <portal-dropdown
                            label={t('tenant-picker-label', locale)}
                            theme={theme}
                            slot="tenant-picker"
                            disabled={!(tenantList && tenantList.length > 0)}
                            class={!this.showSlotEndDivider ? 'tenant-picker-dropdown' : ''}
                            data-cy="tenant-picker-dropdown"
                            outlined={false}
                            list-align="right"
                            id="tenantPicker"
                            placeholder={t('empty-tenant-list-placeholder', locale)}
                            selected={currentTenantInfo}
                            max-list-height="90vh"
                            options={tenantList}
                        />
                    )}
                    { isEnterpriseTrialInternalVisible && (
                        <div slot="enterprise-trial" class="enterprise-trial">
                            {(isHelpIconVisible || isNotificationVisible || isTenantPickerVisible) && (
                                <portal-divider id="enterprise-trial-divider" />
                            )}
                            <portal-enterprise-trial-internal
                                id="enterprise-trial"
                                class="enterprise-trial-internal"
                                end-date={ this.showEnterpriseTrial
                                    ? this.enterpriseTrialEndDate
                                    : accountAndTenantsStore.get('organization')?.endDate ?? 0
                                }
                                subscription-type={accountType}
                                theme={theme}
                            />
                        </div>
                    )}
                    { this.showProfileMenu && (
                        <portal-profile-menu
                            slot="profile-menu"
                            showPreferences={this.showPreferences}
                            data-test="profile-menu"
                            email={this.emailOverride ?? `${sessionStore.get('email') ?? ''}`}
                            fullName={this.fullNameOverride ?? `${sessionStore.get('fullName') ?? ''}`}
                            productInfo={this.productInfo}
                            theme={theme}
                            language={locale}
                        />
                    )}
                    {this.showProfileMenu &&
                        <ap-dialog
                            ref={(elem) => {
                                this.apDialog = elem as HTMLApDialogElement;
                            }}
                            dialogAriaLabel={t('upgrade-trial-dialog-title', locale)}
                        >
                            <ap-upgrade-trial-internal />
                        </ap-dialog>}
                </ap-app-bar>
                <div class="content">
                    {!focusMode && (
                        <ap-nav
                            services={serviceList}
                            theme={theme}
                            isMenuExpanded={expanded}
                        />
                    )}
                    <div class={trialContentClass}>
                        { perServiceTrialEndDate &&
                            <portal-alert-bar
                                theme={theme}
                                class="trial-bar"
                                data-test="trial-bar"
                                status="trial"
                                end-date={perServiceTrialEndDate}
                                cancelable={this.isPerServiceTrialBarCancelable}
                                subscription-type={accountType}
                            />
                        }
                        <slot ref={(el: HTMLSlotElement) => el && (this._slotRef = el)} />
                    </div>
                </div>
                { showTimeoutDialog && <ap-timeout-notice-internal theme={theme} language={locale} /> }
            </Host>
        );
    }

    // #endregion

    // #region Private Methods
    private isFocusMode() {
        return this.focusMode || this.isFocusSize || getFeatureFlag(Features.EnableGlobalFocus.name);
    }

    private isNotificationVisible() {
        const services = getEnvConfigField('Notification_Hide_Services') as string[];
        let disableCurrentService = false;
        if (services !== undefined) {
            // Disable notification if current service found in the array from k8s.
            disableCurrentService = services.includes(store.get('active'));
        }
        return getFeatureFlag(Features.EnableNotification.name)
            && !!accountAndTenantsStore.get('organization')
            && !!getAccessToken()
            && !sessionStore.get('isHost')
            && !disableCurrentService;
    }

    private isTenantPickerVisible(tenantList: Tenant[]) {
        const checkLength = tenantList?.length > 1;
        return !this.hideTenantPicker && !sessionStore.get('isHost') && checkLength;
    }

    private isEnterpriseTrialInternalVisible(accountType?: AccountType) {
        const isActiveTrial = accountType === AccountType.Trial || accountType === AccountType.ProTrial;
        return this.showEnterpriseTrial || (isActiveTrial && AccountAndTenantsInstanceResolver.Instance?.isAdminUserGroup());
    }

    private onLogoClicked(event: CustomEvent, emitter: EventEmitter) {
        event.stopPropagation();
        event.preventDefault();
        emitter.emit({ ...event.detail });
    }

    private onFeatureNameClicked(event: CustomEvent, emitter: EventEmitter) {
        event.preventDefault();
        event.stopPropagation();
        emitter.emit({ ...event.detail });
    }

    // #endregion

    // #region Left Nav Related Private Methods
    private setPortalNavTheme() {
        const validThemeOverride = validateThemeCode(this.themeOverride);
        const shouldUseCachedTheme = !validThemeOverride || validThemeOverride === 'autoTheme';
        const uncachedTheme = validThemeOverride ?? defaultTheme;
        const theme = shouldUseCachedTheme ? getCachedTheme() : uncachedTheme;
        if (shouldUseCachedTheme) {
            setThemeNoBroadcast(store, theme);
        }
        Logger.debug([ 'ap-shell', 'theme' ], `first theme set: ${theme}`, {
            object: {
                shouldUseCachedTheme,
                override: validThemeOverride,
            },
        });
    }

    private setPortalNavLanguage() {
        const env = getEnvironment();
        const defaultLanguage = 'en';

        if (env.includes('gov')) {
            Logger.info([ 'ap-shell', 'language' ], `selecting ${defaultLanguage} as current language because env is ${env}`);
            setLanguageNoBroadcast(store, defaultLanguage);
        } else {
            const cachedLanguage = getCachedLanguage(env);
            const currentLanguage = validateLanguageCode(store.get('overrideLocale')) ?? cachedLanguage ?? defaultLanguage;
            Logger.warn([ 'ap-shell', 'fallback', 'language' ], `selecting current lang: ${currentLanguage}, from store: ${store.get('overrideLocale')}, cache: ${cachedLanguage}, default: ${defaultLanguage}`);
            setLanguageNoBroadcast(store, currentLanguage);
        }
    }

    private getServiceList() {
        let serviceList = accountAndTenantsStore.get('menuItems');
        if (serviceList.length === 0) {
            serviceList = defaultMenuItems;
        }
        serviceList = serviceList.filter(service => service.id !== SERVICE_IDS.help);

        const active = this.active || getSpaceName()?.toLowerCase();
        const handledItems = parseArrayProps(this.emitEventAndPreventNavigationServices);
        const overrideAll = this.emitEventAndPreventNavigationServices === 'all';
        serviceList = serviceList.map(service => ({
            ...service,
            active: caseInsensitiveCompare(service.id, active),
            handled: overrideAll || handledItems.includes(service.id),
        }));

        return serviceList;
    }

    private getValidTrialServiceEndDate() {
        const trialService =
            accountAndTenantsStore.get('organization')?.trialServices?.find(
                service => caseInsensitiveCompare(service?.name ?? '', getSpaceName()),
            );
        return trialService?.endDate;
    }

    private handleFocusSize(isFocusSize: boolean) {
        if (this.isFocusSize !== isFocusSize) {
            this.isFocusSize = isFocusSize;
        }
    }

    private onExpandMenuToggled(event) {
        if (event.detail) {
            this.isMenuExpanded = event.detail;
            return;
        }

        this.isMenuExpanded = !this.isMenuExpanded;
    }

    // #endregion

    // #endregion
}
